import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, Input, HostBinding, ContentChildren, NgModule } from '@angular/core';
import { FormGroup, FormArray, FormControlName } from '@angular/forms';
import * as i1$1 from 'se-fe-form-service';
import { SE_FE_FORM_FIELD_INPUT } from 'se-fe-form-service';

class SeFeFormControlErrorsComponent {
    set messages(value) {
        this._messages = value;
    }
    get messages() {
        return this._messages;
    }
    // This getter parses both regular error keys and field-sepcific error keys generated by the <se-fe-form-fieldset> component.
    // For example the errors ['field:required', 'required'] would first try and find an error message for `field:required` and
    // fall back to a message for `required` if the specific key is not found in the `seFeDataMessages` object.
    get errorMessages() {
        const errors = [...this.seFeDataErrors];
        const messages = [];
        while (errors.length) {
            const key = errors.shift();
            const [field, error] = key.split(':');
            const message = this.messages[key] || this.messages[error ? error : field];
            const errorIndex = error && errors.indexOf(error);
            if (message)
                messages.push(message);
            if (message && errorIndex !== -1)
                errors.splice(errorIndex, 1);
        }
        return messages;
    }
}
SeFeFormControlErrorsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormControlErrorsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SeFeFormControlErrorsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFormControlErrorsComponent, selector: "se-fe-form-control-errors", inputs: { seFeDataErrors: "seFeDataErrors", messages: ["seFeDataMessages", "messages"], seFeDataId: "seFeDataId" }, host: { properties: { "attr.id": "this.seFeDataId" } }, ngImport: i0, template: "<span *ngFor=\"let error of errorMessages\" class=\"se-fe-form-control-errors\">\n  {{ error }}\n</span>\n", styles: [".se-fe-form-control-errors{box-sizing:border-box;color:#b42846;display:block;font-size:calc(var(--se-rem, 1rem) * .8571428571);font-family:\"Open Sans\",sans-serif;margin-top:4px}\n"], directives: [{ type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormControlErrorsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-form-control-errors',
                    templateUrl: './se-fe-form-control-errors.component.html',
                    styleUrls: ['./se-fe-form-control-errors.component.scss'],
                }]
        }], propDecorators: { seFeDataErrors: [{
                type: Input
            }], messages: [{
                type: Input,
                args: ['seFeDataMessages']
            }], seFeDataId: [{
                type: HostBinding,
                args: ['attr.id']
            }, {
                type: Input
            }] } });

class SeFeFormControlHelpComponent {
}
SeFeFormControlHelpComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormControlHelpComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SeFeFormControlHelpComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFormControlHelpComponent, selector: "se-fe-form-control-help", inputs: { seFeDataId: "seFeDataId" }, host: { properties: { "attr.id": "this.seFeDataId" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:block;color:#68767f;font-family:\"Open Sans\",sans-serif;font-size:calc(var(--se-rem, 1rem) * .8571428571);margin:4px 0 0}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormControlHelpComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-form-control-help',
                    template: '<ng-content></ng-content>',
                    styleUrls: ['./se-fe-form-control-help.component.scss'],
                }]
        }], propDecorators: { seFeDataId: [{
                type: HostBinding,
                args: ['attr.id']
            }, {
                type: Input
            }] } });

const OPTIONAL = '(optional)';
class SeFeFormControlLabelComponent {
    constructor() {
        this.optionalText = OPTIONAL;
        this.isRequired = true;
        this.isHidden = false;
    }
    set label(value) {
        this._label = value;
    }
    get label() {
        return !this._label ? this.inputRequired('seFeDataLabel') : this._label;
    }
    get labelForAttr() {
        return this.labelFor ? this.labelFor : '';
    }
    inputRequired(attr) {
        throw new Error(`Attribute ${attr} is required`);
    }
}
SeFeFormControlLabelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormControlLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SeFeFormControlLabelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFormControlLabelComponent, selector: "se-fe-form-control-label", inputs: { label: ["seFeDataLabel", "label"], labelFor: ["seFeDataLabelFor", "labelFor"], isRequired: ["seFeDataRequired", "isRequired"], isHidden: ["seFeDataLabelHidden", "isHidden"] }, ngImport: i0, template: "<label [for]=\"labelForAttr\" \n  class=\"se-fe-form-control-label\"\n  [class.se-fe-sr-only]=\"isHidden\">\n  {{ label }}\n  <small *ngIf=\"!isRequired\">\n    <em>{{ optionalText }}</em>\n  </small>\n</label>\n", styles: [".se-fe-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0}.se-fe-sr-only-focusable:focus{position:relative;width:auto;height:auto;clip:initial;margin:inherit;padding:inherit;border:initial}.se-fe-form-control-label{box-sizing:border-box;color:#68767f;display:block;font-family:\"Open Sans\",sans-serif;font-size:calc(var(--se-rem, 1rem) * 1);margin-bottom:4px}:host-context(.se-fe-form-field--pointer-cursor) .se-fe-form-control-label{cursor:pointer}\n"], directives: [{ type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormControlLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-form-control-label',
                    templateUrl: './se-fe-form-control-label.component.html',
                    styleUrls: ['./se-fe-form-control-label.component.scss'],
                }]
        }], propDecorators: { label: [{
                type: Input,
                args: ['seFeDataLabel']
            }], labelFor: [{
                type: Input,
                args: ['seFeDataLabelFor']
            }], isRequired: [{
                type: Input,
                args: ['seFeDataRequired']
            }], isHidden: [{
                type: Input,
                args: ['seFeDataLabelHidden']
            }] } });

class SeFeFormFieldComponent {
    constructor(formService, element) {
        this.formService = formService;
        this.element = element;
        this.newChildElements = (changes) => {
            const input = this.input = this.selectInput();
            if (!input)
                return;
            this.describedbyId = this.hasDescribedBy() ? this.buildId() : undefined;
            input.setAttribute('id', this.inputId = input.id || this.buildId());
            if (this.describedbyId)
                input.setAttribute('aria-describedby', this.describedbyId);
            else
                input.removeAttribute('aria-describedby');
        };
        this.observeInput();
    }
    ngOnDestroy() {
        if (this.inputObserver)
            this.inputObserver.disconnect();
    }
    buildId() {
        return `se-fe-form-field-${Date.now()}-${SeFeFormFieldComponent.counter++}`;
    }
    get errors() {
        const errors = [];
        const namedErrors = [];
        const nestedInputNames = this.seFeFormControlInputs.map(i => i.formControlNames).filter(x => x);
        const names = [];
        nestedInputNames.forEach(name => names.push(...name));
        const seFeInputControls = [];
        const nestedSeFeInputControls = this.seFeFormControlInputs.map(i => this.getAllFormControls(i.formGroup, names));
        nestedSeFeInputControls.forEach(c => seFeInputControls.push(...c));
        const allControls = [...this.formControlNames, ...seFeInputControls];
        allControls.forEach(fcn => {
            this.formService.controlErrors(fcn).forEach(error => {
                if (!namedErrors.includes(error))
                    namedErrors.push(`${fcn.name}:${error}`);
                if (!errors.includes(error))
                    errors.push(error);
            });
        });
        return [...namedErrors, ...errors];
    }
    getAllFormControls(formGroup, controlNamesToInclude = []) {
        const controls = [];
        //handle FormGroup
        if (formGroup instanceof FormGroup) {
            // Handle FormGroup
            for (const controlName in formGroup.controls) {
                if (formGroup.controls.hasOwnProperty(controlName)) {
                    const control = formGroup.controls[controlName];
                    if (controlNamesToInclude.includes(controlName)) {
                        controls.push({ name: controlName, control });
                    }
                    if (control instanceof FormGroup || control instanceof FormArray) {
                        // get controls from nested FormGroup or FormArray
                        controls.push(...this.getAllFormControls(control, controlNamesToInclude));
                    }
                }
            }
        }
        else if (formGroup instanceof FormArray) {
            // Handle FormArray
            formGroup.controls.forEach(control => {
                if (controlNamesToInclude.includes(control['name'])) {
                    controls.push({ name: control['name'], control });
                }
                if (control instanceof FormGroup || control instanceof FormArray) {
                    // get controls from nested FormGroup or FormArray
                    controls.push(...this.getAllFormControls(control, controlNamesToInclude));
                }
            });
        }
        return controls;
    }
    get isRequired() {
        if (this.config.required !== undefined)
            return this.config.required;
        const controls = this.formControlNames.toArray();
        return !controls.length || !!controls.find(c => this.formService.isRequired(c.control));
    }
    get isLabelHidden() {
        return this.config.hiddenLabel ? true : false;
    }
    select(selector) {
        return this.element.nativeElement.querySelector(selector);
    }
    // finds the most likely HTML input
    selectInput() {
        return this.select('[seFeFormFieldInput]') || // tagged elements first
            this.select('input:not([type="button"]):not([type="submit"]), select, textarea') || // then common inputs
            this.select('button, input') || // then buttons (less likely to be the primary input)
            undefined;
    }
    createObserver(fn) {
        return new MutationObserver(fn);
    }
    observeInput() {
        this.inputObserver = this.createObserver(this.newChildElements);
        this.inputObserver.observe(this.element.nativeElement, {
            childList: true,
            subtree: true
        });
    }
    hasDescribedBy() {
        return !!(this.errors.length || this.select('[seFeHelp]'));
    }
}
SeFeFormFieldComponent.counter = 1;
SeFeFormFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldComponent, deps: [{ token: i1$1.SeFeFormService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
SeFeFormFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFormFieldComponent, selector: "se-fe-form-field", inputs: { config: ["seFeDataConfig", "config"] }, queries: [{ propertyName: "formControlNames", predicate: FormControlName, descendants: true }, { propertyName: "seFeFormControlInputs", predicate: SE_FE_FORM_FIELD_INPUT, descendants: true }], ngImport: i0, template: "<div class=\"se-fe-form-field__main\" [class.se-fe-form-field--pointer-cursor]=\"!!input\">\n  <se-fe-form-control-label\n    class=\"se-fe-form-field__label\"\n    [seFeDataLabel]=\"config.label\"\n    [seFeDataLabelFor]=\"inputId\"\n    [seFeDataLabelHidden]=\"isLabelHidden\"\n    [seFeDataRequired]=\"isRequired\">\n  </se-fe-form-control-label>\n  <ng-content></ng-content>\n  <se-fe-form-control-help\n    *ngIf=\"!errors.length\"\n    [seFeDataId]=\"describedbyId\">\n    <ng-content select=\"[seFeHelp]\"></ng-content>\n  </se-fe-form-control-help>\n  <se-fe-form-control-errors\n    *ngIf=\"errors.length\"\n    [seFeDataMessages]=\"config.errorMessages\"\n    [seFeDataErrors]=\"errors\"\n    [seFeDataId]=\"describedbyId\">\n  </se-fe-form-control-errors>\n</div>\n<div class=\"se-fe-form-field__right\"><ng-content select=\"[seFeRightInput]\"></ng-content></div>\n", styles: [":host{align-items:center;display:flex;flex-direction:row;flex-wrap:wrap;font:normal 400 calc(var(--se-rem, 1rem) * 1)/1.4286 \"Open Sans\",sans-serif}.se-fe-form-field__main{flex:1 1 0;min-width:0}.se-fe-form-field__right{align-self:flex-start;flex:0 0 auto;padding-left:12px}.se-fe-form-field__right:empty{display:none}\n"], components: [{ type: SeFeFormControlLabelComponent, selector: "se-fe-form-control-label", inputs: ["seFeDataLabel", "seFeDataLabelFor", "seFeDataRequired", "seFeDataLabelHidden"] }, { type: SeFeFormControlHelpComponent, selector: "se-fe-form-control-help", inputs: ["seFeDataId"] }, { type: SeFeFormControlErrorsComponent, selector: "se-fe-form-control-errors", inputs: ["seFeDataErrors", "seFeDataMessages", "seFeDataId"] }], directives: [{ type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-form-field',
                    templateUrl: './se-fe-form-field.component.html',
                    styleUrls: ['./se-fe-form-field.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1$1.SeFeFormService }, { type: i0.ElementRef }]; }, propDecorators: { config: [{
                type: Input,
                args: ['seFeDataConfig']
            }], formControlNames: [{
                type: ContentChildren,
                args: [FormControlName, { descendants: true }]
            }], seFeFormControlInputs: [{
                type: ContentChildren,
                args: [SE_FE_FORM_FIELD_INPUT, { descendants: true }]
            }] } });

class SeFeFormFieldsetComponent extends SeFeFormFieldComponent {
}
SeFeFormFieldsetComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldsetComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
SeFeFormFieldsetComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFormFieldsetComponent, selector: "se-fe-form-fieldset", usesInheritance: true, ngImport: i0, template: "<fieldset class=\"se-fe-form-fieldset\">\n  <legend class=\"se-fe-form-fieldset__legend\">\n    {{ config.label }}\n  </legend>\n  <ng-content></ng-content>\n  <se-fe-form-control-help\n    *ngIf=\"!errors.length\"\n    [seFeDataId]=\"describedbyId\">\n    <ng-content select=\"[seFeHelp]\"></ng-content>\n  </se-fe-form-control-help>\n  <se-fe-form-control-errors\n    *ngIf=\"errors.length\"\n    [seFeDataMessages]=\"config.errorMessages\"\n    [seFeDataErrors]=\"errors\"\n    [seFeDataId]=\"describedbyId\">\n  </se-fe-form-control-errors>\n</fieldset>\n", styles: [":host{display:block;font:normal 400 calc(var(--se-rem, 1rem) * 1)/1.4286 \"Open Sans\",sans-serif}.se-fe-form-fieldset{border:none;margin:0;padding:0;min-width:0}.se-fe-form-fieldset__legend{box-sizing:border-box;color:#68767f;display:block;font-family:\"Open Sans\",sans-serif;font-size:calc(var(--se-rem, 1rem) * 1);margin-bottom:4px}\n"], components: [{ type: SeFeFormControlHelpComponent, selector: "se-fe-form-control-help", inputs: ["seFeDataId"] }, { type: SeFeFormControlErrorsComponent, selector: "se-fe-form-control-errors", inputs: ["seFeDataErrors", "seFeDataMessages", "seFeDataId"] }], directives: [{ type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldsetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-form-fieldset',
                    templateUrl: './se-fe-form-fieldset.component.html',
                    styleUrls: ['./se-fe-form-fieldset.component.scss'],
                }]
        }] });

class SeFeFormFieldModule {
}
SeFeFormFieldModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeFormFieldModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldModule, declarations: [SeFeFormFieldComponent,
        SeFeFormFieldsetComponent,
        SeFeFormControlLabelComponent,
        SeFeFormControlHelpComponent,
        SeFeFormControlErrorsComponent], imports: [CommonModule], exports: [SeFeFormFieldComponent,
        SeFeFormFieldsetComponent,
        SeFeFormControlLabelComponent,
        SeFeFormControlHelpComponent,
        SeFeFormControlErrorsComponent] });
SeFeFormFieldModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFormFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SeFeFormFieldComponent,
                        SeFeFormFieldsetComponent,
                        SeFeFormControlLabelComponent,
                        SeFeFormControlHelpComponent,
                        SeFeFormControlErrorsComponent,
                    ],
                    imports: [CommonModule],
                    exports: [
                        SeFeFormFieldComponent,
                        SeFeFormFieldsetComponent,
                        SeFeFormControlLabelComponent,
                        SeFeFormControlHelpComponent,
                        SeFeFormControlErrorsComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { OPTIONAL, SeFeFormControlErrorsComponent, SeFeFormControlHelpComponent, SeFeFormControlLabelComponent, SeFeFormFieldComponent, SeFeFormFieldModule, SeFeFormFieldsetComponent };
